import { useApolloClient, useMutation } from '@apollo/client';
import {
  operations,
  pushMessageToWebViewContainer
} from '@components/appointments/webViewUtil';
import { getPanelErrors } from '@components/appointments/appointmentPanel/ApptPanel';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import usePreferredTimezone from '@hooks/usePreferredTimezone';
import { getAgendaDateInPreferredTimezone } from '@components/appointments/utilities/getAgendaDateInPreferredTimezone';
import { useAgendaItemsCache } from '@components/mySchedule/useAgendaItemsCache';
import { usePageActionsApi } from '@metrics/client/usePageActionsApi';
import { UPDATE_APPOINTMENT } from '@graphql/operations/appointment/updateAppointment.ts';
import { ACCEPT_APPOINTMENT } from '@graphql/operations/appointment/acceptAppointment.ts';
import { updateAgendaDates } from '../../mySchedule/updateAgendaDates';

const LOG = LoggerFactory.create('appointmentMutations');

/**
 * Hook to update appointment
 */
export const useUpdateAppointment = (
  closePanel = () => null,
  onApptSuccessAlert = () => null,
  errors,
  setErrors,
  selectedLocale,
  oldAgendaItemStartTime
) => {
  const { preferredTimezone } = usePreferredTimezone();
  const apolloClient = useApolloClient();
  const updateLocalAgendaItem = useAgendaItemsCache();
  const [updateAppointmentMutation, { loading: updatingAppt }] = useMutation(
    UPDATE_APPOINTMENT,
    {
      update(cache, { data }) {
        if (data.updateAppointment?.id) {
          updateLocalAgendaItem({
            newAgendaItem: { ...data.updateAppointment, type: 'APPOINTMENT' },
            change: 'update',
            oldAgendaItemStartTime
          });
        }
      },
      onCompleted: data => {
        pushMessageToWebViewContainer(
          operations.updateAppt,
          data.updateAppointment
        );
        if (data.updateAppointment?.start) {
          /*
            The dates here are in format - (YYYY-MM-dd) in user's preferred time zone.
            If the newly added or updated agenda is on a date which is not present in the apollo cache corresponding to
            Agenda Dates graphQL query, we want to add the same so that user immediately see the new date on the UI.
            Reference - https://wiki.cvent.com/display/RD/Date+tabs+on+My+Schedule+page+of+Attendee+Hub+-+Web?focusedCommentId=520587905#comment-520587905
           */
          updateAgendaDates(
            apolloClient?.cache,
            getAgendaDateInPreferredTimezone(
              data.updateAppointment.start,
              preferredTimezone
            ),
            preferredTimezone
          );
        }
        closePanel();
        onApptSuccessAlert(operations.updateAppt);
      },
      onError: error => {
        pushMessageToWebViewContainer(
          operations.updateAppt,
          { message: error },
          true
        );
        LOG.error('Error updating appointment', error);
        getPanelErrors({ error, setErrors, errors });
      }
    }
  );
  return { updateAppointmentMutation, updatingAppt };
};

/**
 * Hook to decline/cancel appointment
 */
export const useRemoveAppointment = (
  mutationQuery,
  apptId,
  onComplete,
  handleError,
  locale,
  factOrigin
) => {
  const updateLocalAgendaItem = useAgendaItemsCache();
  const {
    declineAppointment: declineAppointmentAction,
    cancelAppointment: cancelAppointmentAction
  } = usePageActionsApi();
  const [removeAppt, { loading: removing }] = useMutation(mutationQuery, {
    onCompleted: data => {
      onComplete(data);
    },
    update(cache, { data }) {
      if (data.cancelAppointment || data.declineAppointment) {
        updateLocalAgendaItem({
          newAgendaItem: { id: apptId, type: 'APPOINTMENT' },
          change: 'remove'
        });
        if (data.declineAppointment) {
          declineAppointmentAction({ factOrigin });
        } else {
          cancelAppointmentAction({ factOrigin });
        }
      }
    },
    onError: error => {
      LOG.error('Error removing appointment', error);
      handleError(error);
    }
  });

  return { removeAppt, removing };
};

/**
 * Hook to accept appointment
 */
export const useAcceptAppointment = (
  onComplete,
  handleError,
  locale,
  factOrigin,
  apptDetails = {}
) => {
  const updateLocalAgendaItem = useAgendaItemsCache();
  const { acceptAppointment: acceptAppointmentAction } = usePageActionsApi();
  const [acceptAppointment, { loading: accepting }] = useMutation(
    ACCEPT_APPOINTMENT,
    {
      update: (cache, { data }) => {
        if (data.acceptAppointment.id) {
          updateLocalAgendaItem({
            newAgendaItem: data.acceptAppointment,
            change: 'accept'
          });
        }
      },
      onCompleted: data => {
        pushMessageToWebViewContainer(operations.acceptAppt, {
          ...data.acceptAppointment,
          appointmentStatus: apptDetails.appointmentStatus,
          parentLocationDisplayName: apptDetails.parentLocationDisplayName,
          locationDisplayName: apptDetails.locationDisplayName,
          requesters: apptDetails.requesters,
          requestees: apptDetails.requestees
        });
        onComplete();
        acceptAppointmentAction({ factOrigin });
      },
      onError: error => {
        LOG.error('Error accepting appointment', error);
        handleError(error);
      }
    }
  );

  return { acceptAppointment, accepting };
};
